import * as React from 'react'
import { forwardRef, useCallback, useState } from 'react'
import type { JSX } from 'react'

import { useDroppable } from '@dnd-kit/core'
import { clsx } from 'clsx'

import { Flex, Text, useMergeRefs } from '@buffer-mono/popcorn'

import { format } from '~publish/helpers/temporal'

import { AddButton } from './AddButton'
import { useCalendarContext } from './context'
import { DraggableAndDroppableItem } from './DraggableAndDroppableItem'
import { DraggableItem } from './DraggableItem'
import { DroppableItem } from './DroppableItem'
import { Item } from './Item'
import ShowMoreLessButtons from './ShowMoreLessButtons'
import type { CalendarBlockData } from './types'

import styles from './Hour.module.css'

const HOUR_FORMAT = {
  '24': 'H:00',
  '12': 'h a',
}
const DEFAULT_ITEMS_LIMIT = 3
const FIVE_MINUTES_MS = 300000

type HourProps = {
  hour: CalendarBlockData
  showLabel: boolean
  loading: boolean
  index: number
}

const Hour = forwardRef<React.ElementRef<'td'>, HourProps>(
  ({ hour, loading, showLabel = false, index }, forwardedRef): JSX.Element => {
    const {
      itemsByHour,
      is24HourFormat,
      addItemProps,
      currentTime,
      isMobile,
      readOnly,
      preferencesLoading,
    } = useCalendarContext()
    const [limit, setLimit] = useState(DEFAULT_ITEMS_LIMIT)
    const { now, nowTimestamp } = currentTime

    // Disable drop if the hour is in the past and not an skipped DST hour
    const dropDisabled =
      hour.startTimestamp < nowTimestamp && !hour.isDSTSkippedHour
    const hourLabel = format(
      hour.start,
      HOUR_FORMAT[is24HourFormat ? '24' : '12'],
    )
    const addButtonLabel = `${format(
      hour.start,
      'eeee, d MMMM y',
    )} at ${hourLabel}`

    const itemsToRender = itemsByHour?.get(hour.startTimestamp)

    // The 12/24-hour preference decides the label text, so labels hold back
    // until the preference is known. The label container takes no layout
    // space, so nothing shifts when they appear.
    const showHourLabel = showLabel && index % 2 === 0 && !preferencesLoading

    const getCreationTimestamp = useCallback(
      (): number =>
        hour.creationTimestamp && hour.creationTimestamp < nowTimestamp
          ? nowTimestamp + FIVE_MINUTES_MS
          : (hour.creationTimestamp ?? hour.startTimestamp),
      [hour.creationTimestamp, hour.startTimestamp, nowTimestamp],
    )

    const { setNodeRef, isOver, active } = useDroppable({
      id: hour.startTimestamp ?? '',
      disabled: dropDisabled,
      data: {
        type: 'hour',
        timestamp: hour.startTimestamp,
        creationTimestamp: getCreationTimestamp(),
      },
    })

    const mergedRef = useMergeRefs(forwardedRef, setNodeRef)

    const showAddButton =
      !readOnly &&
      !dropDisabled &&
      !loading &&
      !hour.isDSTSkippedHour &&
      !isMobile

    const isClickable =
      isMobile &&
      !readOnly &&
      !dropDisabled &&
      !hour.isDSTSkippedHour &&
      !loading

    const handleHourClick = useCallback(
      (event: React.MouseEvent<HTMLTableCellElement>): void => {
        if (!isClickable) return

        // Only trigger when tapping empty space in the cell
        if (event.target !== event.currentTarget) return

        addItemProps.onClick(getCreationTimestamp())
      },
      [isClickable, addItemProps, getCreationTimestamp],
    )

    const handleHourKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLTableCellElement>): void => {
        if (!isClickable) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          addItemProps.onClick(getCreationTimestamp())
        }
      },
      [isClickable, addItemProps, getCreationTimestamp],
    )

    return (
      <td
        className={clsx(styles.hour, {
          [styles.disabled]: dropDisabled,
          [styles.draggingOver]: isOver,
          [styles.dragHappening]: Boolean(active),
          [styles.dst]: hour.isDSTSkippedHour,
          [styles.hasLabel]: showHourLabel,
          [styles.clickable]: isClickable,
        })}
        ref={dropDisabled ? forwardedRef : mergedRef}
        data-datetime={hour.start.toString()}
        data-index={index}
        onClick={handleHourClick}
        onKeyDown={handleHourKeyDown}
        tabIndex={isClickable ? 0 : undefined}
        role={isClickable ? 'button' : undefined}
        aria-label={
          isClickable ? `Create post at ${addButtonLabel}` : undefined
        }
      >
        {showHourLabel ? (
          <div className={styles.hourLabelContainer}>
            <time dateTime={hour.start.toString()} className={styles.hourLabel}>
              {getHourLabel(index % 24, is24HourFormat)}
            </time>
            <span className={styles.hourLabelSeparator} />
          </div>
        ) : null}
        {hour.isDSTSkippedHour && (
          <div className={styles.dstOverlay}>
            <Text size="sm" color="subtle">
              {isMobile ? 'DST' : 'Daylight Saving Time (skipped hour)'}
            </Text>
          </div>
        )}
        {now &&
          !hour.isDSTSkippedHour &&
          hour.startTimestamp &&
          itemsToRender?.slice(0, limit).map((item, itemIndex) => {
            if (item.draggable && item.droppable) {
              return (
                <DraggableAndDroppableItem
                  key={item.id}
                  id={item.id}
                  index={itemIndex}
                  timestamp={item.timestamp}
                  type={item.type}
                >
                  <Item {...item} />
                </DraggableAndDroppableItem>
              )
            }
            if (item.draggable) {
              return (
                <DraggableItem
                  key={item.id}
                  id={item.id}
                  index={itemIndex}
                  timestamp={item.timestamp}
                  type={item.type}
                  className={clsx(styles.item, {
                    [styles.activeItem]: active?.id === item.id,
                  })}
                  disabled={
                    item.draggable === undefined
                      ? item.timestamp <
                        Math.floor(now.epochMilliseconds / 1000)
                      : !item.draggable
                  }
                >
                  <Item {...item} />
                </DraggableItem>
              )
            }
            if (item.droppable) {
              return (
                <DroppableItem
                  key={item.id}
                  id={item.id}
                  index={itemIndex}
                  timestamp={item.timestamp}
                  type={item.type}
                >
                  <Item {...item} />
                </DroppableItem>
              )
            }
            return <Item {...item} key={item.id} />
          })}
        <Flex gap="xs" justify="between">
          <ShowMoreLessButtons
            itemsToRender={itemsToRender ?? []}
            limit={limit}
            setLimit={setLimit}
            compact={isMobile}
          />
          {showAddButton && hour.creationTimestamp && (
            <AddButton
              timestamp={getCreationTimestamp()}
              labelDate={addButtonLabel}
              className={clsx(styles.addButton, styles.newAddButton)}
            />
          )}
        </Flex>
      </td>
    )
  },
)

Hour.displayName = 'Hour'

const memoHour = React.memo(Hour)
export { memoHour as Hour }

/** "08" / "14" in 24h mode, "12 AM" / "1 PM" in 12h mode. */
function getHourLabel(hour: number, is24HourFormat: boolean): string {
  if (is24HourFormat) {
    return hour.toString().padStart(2, '0')
  }
  return `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? 'AM' : 'PM'}`
}
